import { Helmet } from "react-helmet-async";
import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import SeoAlternates from "@/components/SeoAlternates";
import { useI18n } from "@/i18n/I18nProvider";

const ExternalLink = ({ href, children, className = "text-primary hover:underline" }: { href: string; children: React.ReactNode; className?: string }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
    {children} ↗
  </a>
);

const About = () => {
  const { m } = useI18n();
  const a = m.about;

  return (
    <>
      <Helmet>
        <title>{`${m.meta.aboutTitle} — BioNexus`}</title>
        <meta name="description" content={m.meta.aboutDescription} />
        <meta property="og:title" content={`${m.meta.aboutTitle} — BioNexus`} />
        <meta property="og:description" content={m.meta.aboutDescription} />
        <meta property="og:type" content="website" />
      </Helmet>
      <SeoAlternates path="/about" />

      <div className="min-h-screen bg-background">
        <Header />

        <main className="max-w-[700px] mx-auto px-4 md:px-6">
          <section className="pt-[120px] pb-16 text-center">
            <h1 className="font-display text-5xl font-medium text-foreground">{a.title}</h1>
          </section>

          <section className="pb-16">
            <h2 className="font-display text-3xl font-medium text-foreground mb-4">{a.whatTitle}</h2>
            <p className="text-lg leading-relaxed text-foreground/80">{a.whatText}</p>
          </section>

          <section className="pb-16">
            <h2 className="font-display text-3xl font-medium text-foreground mb-4">{a.whyTitle}</h2>
            <p className="text-lg leading-relaxed text-foreground/80 mb-4">
              {a.why1a}
              <a href="#source-1" className="text-primary hover:underline align-super text-sm">¹</a>
              {a.why1b}
            </p>
            <p className="text-lg leading-relaxed text-foreground/80 mb-6">
              {a.why2a}
              <a href="#source-2" className="text-primary hover:underline align-super text-sm">²</a>
              {a.why2b}
            </p>
            <div className="border-t border-foreground/10 pt-4">
              <p className="text-sm text-foreground/60 mb-2" id="source-1">
                ¹{" "}
                <ExternalLink href="https://www.itu.int/en/ITU-D/Environment/pages/spotlight/global-ewaste-monitor-2020.aspx" className="text-primary/80 hover:underline">
                  {a.source1}
                </ExternalLink>
                {a.source1After}
              </p>
              <p className="text-sm text-foreground/60" id="source-2">
                ²{" "}
                <ExternalLink href="https://www.grandviewresearch.com/industry-analysis/biodegradable-electronics-polymers-market-report" className="text-primary/80 hover:underline">
                  {a.source2}
                </ExternalLink>
                {a.source2After}
              </p>
            </div>
          </section>

          <section className="pb-16">
            <h2 className="font-display text-3xl font-medium text-foreground mb-4">{a.approachTitle}</h2>
            <p className="text-lg leading-relaxed text-foreground/80 mb-6">{a.approachText}</p>
            <ul className="space-y-2 mb-6">
              {a.approachList.map((item) => (
                <li key={item} className="text-base text-foreground/80 flex items-start gap-2">
                  <span className="text-primary">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-lg leading-relaxed text-foreground/80">{a.approachNote}</p>
          </section>

          <section className="pb-16">
            <h2 className="font-display text-3xl font-medium text-foreground mb-6">{a.resourcesTitle}</h2>
            <div className="mb-6">
              <h3 className="font-sans text-lg font-semibold text-foreground mb-3">{a.databases}</h3>
              <ul className="space-y-2">
                <li className="text-base text-foreground/80">
                  • <ExternalLink href="https://www.futurematerialsbank.com/materials/">Future Material Bank</ExternalLink> — {a.fmb}
                </li>
                <li className="text-base text-foreground/80">
                  • <ExternalLink href="https://materiom.org">Materiom</ExternalLink> — {a.materiom}
                </li>
              </ul>
            </div>
          </section>

          <section className="pb-16">
            <h2 className="font-display text-3xl font-medium text-foreground mb-4">{a.contributeTitle}</h2>
            <p className="text-lg leading-relaxed text-foreground/80 mb-6">{a.contributeText}</p>
            <a
              href="mailto:submit@bionexus.io"
              className="inline-block rounded-full border border-primary text-primary px-6 py-3 text-base font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              {a.submit} →
            </a>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default About;
